import { lazy, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Portal, Snackbar, Typography } from "@mui/material";

import { useToimitusData } from "src/context/ToimitusContext";
import { useKeikkaData } from "src/context/KeikkaDataContext";
import { useUserData } from "src/context/userContext";
import { useStepLogData } from "src/context/StepLogContext";
import { useStatusData } from "src/context/StatusContext";
import {
  kunPumppuToimittajaVastaanottaaTilauksen,
  saveKeikkaTilaToDB_exported,
} from "src/data/keikka/handleKeikka";
import {
  deleteStepLogMessagesByType,
  newStepLogMessage,
} from "src/data/stepLog/stepLogMessageClass";
import { showKeikkaLaskutusChain, showNotificationsButton } from "src/data/ifs/showIfs";

import StepButtons from "src/components/buttons/StepButtons/StepButtons";
import AikaRow from "../Rows/AikaRow/AikaRow";
import TyomaaRow from "../Rows/TyomaaRow/TyomaaRow";
import TilausRowWrapper from "../Rows/TilausRowWrapper";
import HuomRow from "../Rows/HuomRow/HuomRow";
import HenkiloRow from "../Rows/HenkiloRow/HenkiloRow";
import MyDivider from "src/components/layout/MyDivider";
import TilausnumeroRow from "../Rows/TilausnumeroRow/TilausnumeroRow";
import TilaRow from "../Rows/TilaRow/TilaRow";
import RefreshIcon from "src/components/layout/RefreshButton/RefreshIcon";
import { isDesktop } from "react-device-detect";
import SendTilaustiedotButton from "src/components/comms/SendTilaustiedotButton/SendTilaustiedotButton";
import KeikkaIcon2 from "src/components/keikka/keikkaIcon/KeikkaIcon2";
import TransitionIn from "src/components/system/TransitionIn";
import LazyLoader from "src/components/layout/Progress/LazyLoader";
import KeikkaOtsikkoLayout from "src/components/keikka/keikkaOtsikko/KeikkaOtsikkoLayout";
import PrintButton from "src/components/buttons/PrintButton";
import LaskuRow from "../Rows/LaskuRow/LaskuRow";

const _confirmRightButtonModal = import("../Modals/confirmKeikka/ConfirmRightButtonModal");
const ConfirmRightButtonModal = lazy(() => _confirmRightButtonModal);
const _handleKeikkaModal = import(
  "src/pages/steps/StepTilaus/Modals/handleKeikka/HandleKeikkaModal"
);
const HandleKeikkaModal = lazy(() => _handleKeikkaModal);
const _pumppausRow = import("../Rows/PumppausRow/PumppausRow");
const PumppausRow = lazy(() => _pumppausRow);
const _betoniRows = import("../Rows/BetoniRow/BetoniRows");
const BetoniRows = lazy(() => _betoniRows);

const NotificationsButton = lazy(() =>
  import("src/components/comms/NotificationsButton/NotificationsButton")
);

const styles = {
  stepButtons: { paddingLeft: "12px", paddingRight: "12px" },
};

export default function StepTilaus(props) {
  const keikkaIconSize = 140;

  const { user, token, isLoggedIn, socket } = useUserData();
  const { toimitus } = useToimitusData();
  const { keikkaData, doSetKeikkaData, reloadKeikkaData } = useKeikkaData();
  const { stepLog } = useStepLogData();
  const { isLoadedPercent, isLoading } = useStatusData();
  const { reloadToimitus } = useToimitusData();

  const [confirmRightButtonModalIsOpen, setConfirmRightButtonModalIsOpen] = useState(false);
  const [handleKeikkaModalIsOpen, setHandleKeikkaModalIsOpen] = useState(false);
  const [isTarjouspyyntö, setIsTarjouspyyntö] = useState(false);
  const [snackbarIsOpen, setSnackbarIsOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // setIsTarjouspyyntö
  useEffect(() => {
    if (!stepLog) return;
    if (!keikkaData) return;
    // get isTarjouspyyntöChecked from stepLog context when tila is Luonnos
    if (keikkaData.keikkaId === 0) return;
    if (keikkaData.tila.keikkaTilaId === 0) {
      setIsTarjouspyyntö(stepLog && stepLog.isTarjouspyyntö);
    }
  }, [stepLog, keikkaData, keikkaData?.keikkaId, keikkaData?.tila?.keikkaTilaId]);

  const navTo = (value) => props.navTo(value);
  const handleLeftButtonKlik = () => props.onLeftButtonKlik();

  const leftButtonText = () => {
    const etuvisulle = props.leftButtonText ? props.leftButtonText : "PALAA ETUSIVULLE";
    return etuvisulle;
  };

  const peruTxt = "PERU TILAUS";
  const kopioiTxt = "TILAA UUDESTAAN";
  const lähetäTxt = "LÄHETÄ TILAUS";
  const vahvistaTxt = "VAHVISTA TILAUS";
  const vastaanotaTxt = "VASTAANOTA TILAUS";
  const getActionText = (keikkaData) => {
    if (keikkaData.tila.muokkausTila) return "LOPETA MUOKKAUSTILA";
    const isKeikkaAdmin = keikkaData.auth.isKeikkaAdmin;
    let _action = isKeikkaAdmin ? keikkaData.tila.mainAdminAction : keikkaData.tila.mainAction;
    if (_action === "uudestaan") return kopioiTxt;
    if (_action === "peru") return peruTxt;
    if (_action === "lähetä") return lähetäTxt;
    if (_action === "hyväksy") return vahvistaTxt;
    if (_action === "vastaanota") return vastaanotaTxt;
    // if no action, then return null;
    return null;
  };

  const rightButtonText = (keikkaData) => {
    return getActionText(keikkaData);
  };

  const handleRefreshKlik = async () => {
    keikkaData.isLoading = true;
    doSetKeikkaData(keikkaData);
    reloadKeikkaData(keikkaData, user, true).then(() => doSetKeikkaData(keikkaData));
    keikkaData.modules.hasToimitus && reloadToimitus();
  };
  const handleRightButtonKlik = async () => {
    // jos muokkaustila niin lopeta muokkaustila ja return
    if (keikkaData.tila.muokkausTila) {
      keikkaData.tila.muokkausTila = false;
      keikkaData.auth.edit = false;
      keikkaData.tyomaa.auth.edit = false;
      keikkaData.asiakas.auth.edit = false;
      doSetKeikkaData(keikkaData);
      reloadKeikkaData(keikkaData, user, false).then(() => doSetKeikkaData(keikkaData));
      return;
    }
    if (keikkaData.auth.isKeikkaAdmin) {
      // jos vastaanottaa tilausta
      if (keikkaData.tila.mainAdminAction === "vastaanota") {
        // Tila=2: odottaa vastaanottajaa
        // Tilaus siirtyy suoraan tilaan 3:käsittelyssä
        kunPumppuToimittajaVastaanottaaTilauksen(keikkaData, user.personId, token, socket).then(
          () => {
            doSetKeikkaData(keikkaData);
          }
        );
        handleSnackbarOpen("Tilaus vastaanotettu");
        return true;
      }
      if (keikkaData.tila.keikkaTilaId < 4) {
        // Tila=3:
        if (keikkaData.pumppu.lopetusAikaIsInPast) {
          console.log("keikkaData.pumppu.lopetusAikaIsInPast, set tila to 9");
          keikkaData.tila.keikkaTilaId = 9;
          doSetKeikkaData(keikkaData);
          await saveKeikkaTilaToDB_exported(keikkaData, user.personId, token, socket);
        } else {
          // vehicle pitäisi olla jo haettu, poista alla olevat kommentoidut rivit
          //käsittelyssä, niin hae vehicle
          //const v = await vehicle_fetch(keikkaData.pumppu.vehicle.vehicleId, token);
          //keikkaData.pumppu.vehicle.fetchVehicle(token).then(() => {
          //  doSetKeikkaData(keikkaData);
          //});
          setHandleKeikkaModalIsOpen(true);
        }
        return true;
      }
    }
    setConfirmRightButtonModalIsOpen(true);
  };

  const rightButtonHidden = () => {
    if (props.hideRightButton) return true;
    if (keikkaData.isLoading) return true;
    if (keikkaData.auth.isKeikkaAdmin) return false;
    if (keikkaData.keikkaError() && keikkaData.tila.keikkaTilaId < 6) return true;
    if (keikkaData.tila.keikkaTilaId > 5) {
      if (keikkaData.tyomaa.auth.createKeikka) {
        return false;
      } else {
        return true;
      }
    }
    return false;
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarIsOpen(false);
  };

  const handleSnackbarOpen = (message) => {
    setSnackbarMessage(message);
    setSnackbarIsOpen(true);
  };

  return (
    <>
      <Portal>
        <Snackbar
          open={snackbarIsOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}>
          <Alert onClose={handleSnackbarClose} severity="success">
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Portal>
      <LazyLoader>
        <HandleKeikkaModal
          open={handleKeikkaModalIsOpen}
          closeModal={() => setHandleKeikkaModalIsOpen(false)}
        />
      </LazyLoader>
      <LazyLoader>
        <ConfirmRightButtonModal
          open={confirmRightButtonModalIsOpen}
          closeModal={() => setConfirmRightButtonModalIsOpen(false)}
          isTarjouspyyntöChecked={isTarjouspyyntö}
        />
      </LazyLoader>
      <div className="spaceAround">
        {isLoggedIn && (
          <>
            <div className="noPrint">
              <div className="noPrint" style={{ display: "flex", paddingLeft: "5px" }}>
                <RefreshIcon
                  onClick={handleRefreshKlik}
                  isLoading={
                    isLoading ||
                    !keikkaData ||
                    keikkaData.isLoading ||
                    keikkaData.isBackgroundLoading
                  }
                  connected={keikkaData && socket && socket.connected}
                  aria-label="Päivitä tilaustiedot"
                  isLoadedPercent={isLoadedPercent}
                  disabled={
                    !keikkaData ||
                    !toimitus ||
                    keikkaData.isBackgroundLoading ||
                    !toimitus.isLoaded ||
                    !stepLog?.isLoaded
                  }
                />
                {showNotificationsButton(keikkaData) && (
                  <LazyLoader>
                    <NotificationsButton aria-label="Näytä ilmoitukset" />
                  </LazyLoader>
                )}
              </div>
            </div>
            <div className="onlyPrint" style={{ opacity: 0.2 }}>
              <KeikkaIcon2 keikkaData={keikkaData} size={keikkaIconSize} mirror />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
              }}>
              <div>
                <div style={{ paddingTop: "20px" }}>
                  <Typography
                    variant="h6"
                    align="center"
                    color="primary"
                    className="fixPrint transitionElement">
                    {stepLog && stepLog.isTarjouspyyntö ? "Tarjouspyyntö" : ""}
                    {/* Tilauksen yhteenveto */}
                  </Typography>
                  <Typography
                    variant="h5"
                    align="center"
                    color="primary"
                    gutterBottom={keikkaData?.keikkaOtsikko != ""}
                    className="fixPrint transitionElement">
                    {keikkaData?.getTilausOtsikko}
                  </Typography>
                </div>
                <KeikkaOtsikkoLayout />
              </div>
            </div>
            <div className="onlyPrint" style={{ opacity: 0.2 }}>
              <KeikkaIcon2 keikkaData={keikkaData} size={keikkaIconSize} />
            </div>
            <div className="noPrint">
              <div style={{ display: "flex" }}>
                <SendTilaustiedotButton buttonText="" showIcon keikkaData={keikkaData} />
                <div className="onlyWide">
                  {isDesktop && <PrintButton isLoading={!keikkaData || keikkaData.isLoading} />}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      <MyDivider gutterTop gutterHeight="6px" />
      <MyDivider gutterTop gutterHeight="1px" />
      {isLoggedIn && keikkaData && keikkaData.keikkaErrorTxt == null ? (
        <>
          <TilausRowWrapper>
            <TilausnumeroRow
              setMuokkaustila={props.setMuokkaustila}
              isTarjouspyyntöChecked={isTarjouspyyntö}
              setIsTarjouspyyntö={handleTarjouspyyntöCheckChange}
            />
          </TilausRowWrapper>
          <TilausRowWrapper>
            <TilaRow
              navTo={navTo}
              buttonText={rightButtonText(keikkaData)}
              buttonClick={handleRightButtonKlik}
              isTarjouspyyntö={isTarjouspyyntö}
            />
          </TilausRowWrapper>
          <TilausRowWrapper>
            <TransitionIn initialHeight={60}>
              <AikaRow onClick={() => navTo(3)} aria-label="Muokkaa aikaa" />
            </TransitionIn>
          </TilausRowWrapper>
          <TilausRowWrapper>
            <TransitionIn initialHeight={70}>
              <TyomaaRow onClick={() => navTo(1)} aria-label="Muokkaa työmaata" />
            </TransitionIn>
          </TilausRowWrapper>
          {keikkaData.modules.hasPumppu && (
            <TilausRowWrapper>
              <LazyLoader>
                <TransitionIn initialHeight={77}>
                  <PumppausRow onClick={() => navTo(2)} aria-label="Muokkaa pumppausta" />
                </TransitionIn>
              </LazyLoader>
            </TilausRowWrapper>
          )}
          {keikkaData.modules.hasBetoni && (
            <TilausRowWrapper>
              <LazyLoader>
                <TransitionIn initialHeight={100}>
                  <BetoniRows />
                </TransitionIn>
              </LazyLoader>
            </TilausRowWrapper>
          )}
          <TilausRowWrapper>
            <TransitionIn initialHeight={81}>
              <HenkiloRow />
            </TransitionIn>
          </TilausRowWrapper>
          {showKeikkaLaskutusChain(keikkaData) && (
            <TilausRowWrapper show={keikkaData.auth.isKeikkaAdmin === true} noPrint>
              <LaskuRow keikkaData={keikkaData} setKeikkaData={doSetKeikkaData} />
            </TilausRowWrapper>
          )}
          <TilausRowWrapper noDivider>
            <HuomRow keikkaData={keikkaData} setKeikkaData={doSetKeikkaData} />
          </TilausRowWrapper>
          {/* 
          <TilausRowWrapper show={ user.isAnyAdmin} noDivider>
            <PrivateCommentsRow />
          </TilausRowWrapper> 
          */}
        </>
      ) : (
        <Typography align="center" color="warning.main">
          {!keikkaData && "Ladataan keikkaa"}
          {keikkaData && keikkaData.keikkaErrorTxt}
        </Typography>
      )}
      {keikkaData && !keikkaData.isLoading && (
        <>
          <div style={styles.stepButtons}>
            <StepButtons
              leftButtonText={leftButtonText(keikkaData)}
              onLeftButtonKlik={handleLeftButtonKlik}
              rightButtonText={rightButtonText(keikkaData)}
              onRightButtonKlik={() => handleRightButtonKlik()}
              leftButtonDisabled={keikkaData.isLoading}
              rightButtonDisabled={rightButtonHidden()}
              rightButtonToolTip={
                keikkaData.isLoading ||
                keikkaData.keikkaError(keikkaData.tila.keikkaTilaId < 5) ||
                null
              }
              enableButtonAfterKlik
            />
          </div>
        </>
      )}
    </>
  );

  function handleTarjouspyyntöCheckChange(_isChecked) {
    setIsTarjouspyyntö(_isChecked);
    if (_isChecked) {
      const sl = newStepLogMessage("is tarjouspyyntö", user.personId, keikkaData, 27);
      sl.isConfirmed = true;
      sl.requireConfirm = false;
      sl.isSystemMessage = true;
      sl.saveToDB(socket, true);
    } else {
      deleteStepLogMessagesByType(keikkaData.keikkaId, 27, socket);
    }
  }
}
StepTilaus.propTypes = {
  navTo: PropTypes.func,
  onLeftButtonKlik: PropTypes.func,
  onRightButtonKlik: PropTypes.func,
  leftButtonText: PropTypes.string,
  reloadKeikkaData: PropTypes.func,
  keikkaId: PropTypes.number,
  hideRightButton: PropTypes.bool,
  setMuokkaustila: PropTypes.func,
  openStepModal: PropTypes.func,
  setOrigBetoniData: PropTypes.func,
};
